/**
 * FontPreview.jsx — Isolated preview to help the owner pick a font
 * for the Thawany design system. Shows identical Arabic content in
 * three candidate fonts side-by-side, all on the approved cream +
 * white palette.
 */
import React, { useEffect } from "react";

const FONTS = [
  {
    id: "A", name: "El Messiri", family: "'El Messiri', serif",
    tag: "🌟 التوصية", desc:
      "أنيق حرفي بلمسة تراثية دافئة. يناسب دفء المقهى ويبقى حديثًا مقروءًا.",
  },
  {
    id: "B", name: "IBM Plex Sans Arabic", family: "'IBM Plex Sans Arabic', sans-serif",
    tag: "تقني نظيف", desc:
      "جيومتري حديث محايد. يجعل ثواني تبدو منتجًا برمجيًا أكثر من مقهى.",
  },
  {
    id: "C", name: "Almarai", family: "'Almarai', sans-serif",
    tag: "سعودي مألوف", desc:
      "مصمَّم سعوديًا، ودود ومألوف محليًا. يميل للحياد أكثر من الدفء.",
  },
];

const SAMPLE = {
  h1: "قاموس المؤشرات التسويقية",
  h2: "الشرائح الثمانية للعملاء",
  body: "صافي المبيعات لهذا الأسبوع بلغ 1842 ريالًا سعوديًا، بارتفاع نسبته 12٪ مقارنة بالأسبوع الماضي. عدد العملاء الفريدين 245 عميلًا.",
  small: "المصدر: فواتير رتم الحيّة · آخر مزامنة قبل دقائق · نافذة 90 يومًا",
  cta: "إطلاق حملة",
};

export default function FontPreview() {
  useEffect(() => {
    // Inject Google Fonts for the two options not already loaded.
    const id = "font-preview-extras";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+Arabic:wght@400;500;600;700&family=Almarai:wght@400;700;800&display=swap";
    document.head.appendChild(link);
  }, []);

  return (
    <div dir="rtl" data-testid="font-preview-page" style={{
      minHeight: "100vh", background: "#F7F2EC", color: "#3D2A1C",
      padding: "40px 24px",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Noto Naskh Arabic', sans-serif",
    }}>
      <div style={{ maxWidth: 1120, margin: "0 auto" }}>
        <header style={{ marginBottom: 28 }}>
          <span style={{
            padding: "4px 10px", borderRadius: 999,
            background: "rgba(170,149,102,0.14)", color: "#AA9566",
            fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
          }}>PREVIEW</span>
          <h1 style={{ margin: "10px 0 6px 0", fontSize: 28, fontWeight: 800,
                        letterSpacing: -0.6 }}>
            اختيار خط ثواني
          </h1>
          <p style={{ margin: 0, fontSize: 14, color: "#78716C" }}>
            نفس المحتوى بالخطوط الثلاثة — قارن، ثم أرسل حرف الخيار (A / B / C).
          </p>
        </header>

        <section style={{
          display: "grid", gap: 18,
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
        }}>
          {FONTS.map((f) => (
            <article key={f.id}
              data-testid={`font-card-${f.id}`}
              style={{
                background: "#FFFFFF",
                border: "1px solid rgba(170,149,102,0.35)",
                borderRadius: 18, padding: "22px 22px",
                boxShadow: "0 6px 20px rgba(61,42,28,0.06)",
                fontFamily: f.family,
              }}>
              <header style={{
                display: "flex", justifyContent: "space-between",
                alignItems: "baseline", marginBottom: 14,
                borderBottom: "1px solid rgba(61,42,28,0.10)",
                paddingBottom: 12,
              }}>
                <div>
                  <div style={{
                    fontSize: 11, color: "#78716C", fontWeight: 700,
                    letterSpacing: 0.4, marginBottom: 4,
                    fontFamily: "-apple-system, sans-serif",
                  }}>
                    الخيار {f.id} · {f.tag}
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: "#AA9566" }}>
                    {f.name}
                  </div>
                </div>
              </header>

              <p style={{
                margin: 0, fontSize: 12.5, color: "#78716C", lineHeight: 1.7,
                fontFamily: "-apple-system, sans-serif",
              }}>
                {f.desc}
              </p>

              <hr style={{
                border: "none", borderTop: "1px dashed rgba(61,42,28,0.12)",
                margin: "16px 0",
              }} />

              <h2 style={{ margin: 0, fontSize: 26, fontWeight: 800,
                            letterSpacing: -0.3, color: "#3D2A1C" }}>
                {SAMPLE.h1}
              </h2>
              <h3 style={{ margin: "8px 0 12px 0", fontSize: 16,
                            fontWeight: 700, color: "#AA9566" }}>
                {SAMPLE.h2}
              </h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.9, color: "#3D2A1C" }}>
                {SAMPLE.body}
              </p>
              <p style={{ margin: "12px 0 0 0", fontSize: 12, color: "#78716C" }}>
                {SAMPLE.small}
              </p>

              <div style={{ marginTop: 18 }}>
                <span style={{
                  display: "inline-block",
                  padding: "8px 16px", borderRadius: 12,
                  background: "#0F766E", color: "#FFFFFF",
                  fontSize: 13, fontWeight: 700, letterSpacing: 0.2,
                }}>
                  {SAMPLE.cta}
                </span>
                <span style={{
                  marginInlineStart: 10, display: "inline-block",
                  padding: "6px 12px", borderRadius: 999,
                  background: "rgba(170,149,102,0.14)", color: "#AA9566",
                  fontSize: 12, fontWeight: 700,
                }}>
                  ذهبي VIP · 245 عميل
                </span>
              </div>
            </article>
          ))}
        </section>

        <footer style={{
          marginTop: 32, fontSize: 12, color: "#A8A29E", textAlign: "center",
        }}>
          مرجع اللوحة: <code>/app/memory/DESIGN_SYSTEM.md</code>
        </footer>
      </div>
    </div>
  );
}
